/**
 * Centralized URL routing utilities for the application.
 * Uses hash-based routing for PWA compatibility on iOS.
 * URLs look like: /#/tasks/inbox, /#/calendar/week, /#/account/all/folder/inbox
 */

import type { MailView, Workspace } from '@/stores/uiStore';
import type { TodoPrimaryView } from '@/types/todo';

/**
 * Mail sub-views with their own hash routes. Mailbox itself routes through the
 * account/folder grammar ('#/', '#/account/…'); the rest are fixed paths.
 */
export const MAIL_VIEW_PATHS: Record<Exclude<MailView, 'mailbox'>, string> = {
  briefing: '#/briefing',
  triage: '#/triage',
  surveys: '#/surveys',
  subscriptions: '#/subscriptions',
};

const MAIL_VIEW_SEGMENTS: Record<string, Exclude<MailView, 'mailbox'>> = {
  briefing: 'briefing',
  triage: 'triage',
  surveys: 'surveys',
  subscriptions: 'subscriptions',
};

/**
 * Parse the current hash URL into pathname and search components.
 * Hash format: #/path?query=value
 */
export function getHashRoute(): { pathname: string; search: string } {
  const hash = window.location.hash;

  // Remove leading # and handle empty hash
  const hashContent = hash.startsWith('#') ? hash.slice(1) : hash;

  if (!hashContent || hashContent === '/') {
    return { pathname: '/', search: '' };
  }

  // Split into path and query string
  const queryIndex = hashContent.indexOf('?');
  if (queryIndex === -1) {
    return { pathname: hashContent, search: '' };
  }

  return {
    pathname: hashContent.slice(0, queryIndex),
    search: hashContent.slice(queryIndex),
  };
}

export interface MailRouteParams {
  accountId: string | null;
  folderId: string | null;
  emailId: string | null;
  threadId: string | null;
  detailAccountId: string | null;
  senderGroupEmail: string | null;
  autoLabelId: string | null;
  senderDigestVisible: boolean;
}

export interface ParsedRoute {
  workspace: Workspace;
  // Mail workspace sub-view (tab); defaults to 'mailbox' for mail routes
  mailView?: MailView;
  // Mail workspace
  mailRoute?: MailRouteParams;
  // Tasks workspace
  tasksRoute?: {
    view: TodoPrimaryView | null;
    taskId: string | null;
    listId: string | null;
    projectId: string | null;
  };
  // Calendar workspace
  calendarRoute?: {
    date: string | null;
    eventId: string | null;
    view: 'day' | 'week' | 'month' | 'year' | 'agenda' | null;
  };
  // Notes workspace
  notesRoute?: {
    noteId: string | null;
    tagId: string | null;
  };
  // Search
  searchQuery?: string;
}

/**
 * Parse the current URL to determine workspace and route details
 */
export function parseUrlRoute(pathname: string, search: string): ParsedRoute {
  const segments = pathname.split('/').filter(Boolean);
  const params = new URLSearchParams(search);

  // Default to mail workspace
  if (segments.length === 0 || segments[0] === '') {
    return { workspace: 'mail', mailView: 'mailbox' };
  }

  const firstSegment = segments[0];

  // Tasks workspace
  if (firstSegment === 'tasks') {
    const tasksRoute = parseTasksRoute(segments.slice(1), params);
    return { workspace: 'todo', tasksRoute };
  }

  // Calendar workspace
  if (firstSegment === 'calendar') {
    const calendarRoute = parseCalendarRoute(segments.slice(1), params);
    return { workspace: 'calendar', calendarRoute };
  }

  // Notes workspace
  if (firstSegment === 'notes') {
    const notesRoute = parseNotesRoute(segments.slice(1), params);
    return { workspace: 'notes', notesRoute };
  }

  // Mail sub-views with fixed routes (briefing / triage / surveys / subs)
  const mailViewSegment = MAIL_VIEW_SEGMENTS[firstSegment];
  if (mailViewSegment) {
    return { workspace: 'mail', mailView: mailViewSegment };
  }

  // Search (query param based)
  const searchQuery = params.get('q');
  if (searchQuery) {
    return { workspace: 'mail', mailView: 'mailbox', searchQuery };
  }

  // Mail workspace (default or explicit /mail prefix)
  const mailSegments = firstSegment === 'mail' ? segments.slice(1) : segments;
  const mailRoute = parseMailRoute(mailSegments, params);
  return { workspace: 'mail', mailView: 'mailbox', mailRoute };
}

/**
 * Parse mail route from path segments and query params.
 * Handles both current path-based format and legacy query-param/mailbox formats.
 */
function parseMailRoute(segments: string[], params: URLSearchParams): MailRouteParams {
  let accountId: string | null = null;
  let folderId: string | null = null;
  let emailId: string | null = null;
  let threadId: string | null = null;

  const decode = (segment: string | undefined): string | null => {
    if (!segment) return null;
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  };

  // Parse path: /account/{id}/folder/{id}/email/{id} or /thread/{id}
  if (segments[0] === 'account' && segments[1]) {
    accountId = decode(segments[1]);
    for (let i = 2; i < segments.length; i += 2) {
      const key = segments[i];
      const value = decode(segments[i + 1]);
      if (!key || !value) continue;
      if (key === 'folder') folderId = value;
      else if (key === 'email') emailId = value;
      else if (key === 'thread') threadId = value;
    }
  } else if (segments[0] === 'mailbox' && segments[1]) {
    // Legacy format: /mailbox/{accountId}/{folderId}/email/{emailId}
    accountId = decode(segments[1]);
    folderId = decode(segments[2]);
    if (segments[3] === 'email' && segments[4]) {
      emailId = decode(segments[4]);
    } else if (segments[3] === 'thread' && segments[4]) {
      threadId = decode(segments[4]);
    }
  } else if (segments[0] === 'thread' && segments[1]) {
    threadId = decode(segments[1]);
  } else if (segments[0] === 'email' && segments[1]) {
    emailId = decode(segments[1]);
  }

  // Also check legacy query params for backward compatibility
  accountId = accountId || params.get('accountId') || params.get('account');
  folderId = folderId || params.get('folderId') || params.get('folder');
  emailId = emailId || params.get('emailId') || params.get('email');
  threadId = threadId || params.get('threadId') || params.get('thread');
  const detailAccountId = params.get('detailAccountId') || params.get('emailAccountId');

  // Parse sender group email
  const senderGroupEmail = params.get('sender');

  // Parse label filter and digest visibility
  const autoLabelId = params.get('label');
  const senderDigestVisible = params.get('digest') === 'true';

  return { accountId, folderId, emailId, threadId, detailAccountId, senderGroupEmail, autoLabelId, senderDigestVisible };
}

/**
 * Check if a mail route has any meaningful navigation params (account, folder, email, or thread).
 */
export function hasMailRouteParams(route: MailRouteParams): boolean {
  return Boolean(route.accountId || route.folderId || route.emailId || route.threadId);
}

/**
 * Parse a full mail route from pathname and search string.
 * Merges path-based params with query params, with path taking precedence for core routing.
 * Returns null if no mail route params are present.
 */
export function parseFullMailRoute(pathname: string, search: string): MailRouteParams | null {
  const segments = pathname.split('/').filter(Boolean);
  const params = new URLSearchParams(search);

  // Skip non-mail workspace prefixes and fixed mail sub-view routes (those
  // carry no account/folder params of their own)
  const firstSegment = segments[0];
  if (
    firstSegment === 'tasks' ||
    firstSegment === 'calendar' ||
    firstSegment === 'notes' ||
    MAIL_VIEW_SEGMENTS[firstSegment]
  ) {
    return null;
  }

  const mailSegments = firstSegment === 'mail' ? segments.slice(1) : segments;
  const route = parseMailRoute(mailSegments, params);

  return hasMailRouteParams(route) ? route : null;
}

/**
 * Build the path portion (without query params) of a mail hash URL.
 */
export function buildMailboxHashPath(
  accountId: string | null,
  folderId: string | null,
  emailId: string | null,
  threadId: string | null
): string {
  const segments: string[] = [];
  const encode = (value: string) => encodeURIComponent(value);

  if (accountId) {
    segments.push('account', encode(accountId));
    if (folderId) {
      segments.push('folder', encode(folderId));
      if (emailId) {
        segments.push('email', encode(emailId));
      } else if (threadId) {
        segments.push('thread', encode(threadId));
      }
    } else if (threadId) {
      segments.push('thread', encode(threadId));
    } else if (emailId) {
      segments.push('email', encode(emailId));
    }
  } else if (threadId) {
    segments.push('thread', encode(threadId));
  } else if (emailId) {
    segments.push('email', encode(emailId));
  }

  if (!segments.length) {
    return '#/';
  }

  return `#/${segments.join('/')}`;
}

/**
 * Parse tasks route segments
 */
function parseTasksRoute(segments: string[], params: URLSearchParams) {
  let view: TodoPrimaryView | null = null;
  let taskId: string | null = null;
  let listId: string | null = null;
  let projectId: string | null = null;

  if (segments.length === 0) {
    view = 'inbox'; // Default view
  } else if (segments[0] === 'inbox') {
    view = 'inbox';
  } else if (segments[0] === 'today') {
    view = 'today';
  } else if (segments[0] === 'upcoming') {
    view = 'upcoming';
  } else if (segments[0] === 'anytime') {
    view = 'anytime';
  } else if (segments[0] === 'someday') {
    view = 'someday';
  } else if (segments[0] === 'logbook') {
    view = 'logbook';
  } else if (segments[0] === 'trash') {
    view = 'trash';
  } else if (segments[0] === 'list' && segments[1]) {
    listId = decodeURIComponent(segments[1]);
    view = 'anytime'; // List view uses anytime
  } else if (segments[0] === 'project' && segments[1]) {
    projectId = decodeURIComponent(segments[1]);
    view = 'anytime'; // Project view uses anytime
  } else {
    // Assume it's a task ID
    taskId = decodeURIComponent(segments[0]);
  }

  // Also check query params
  taskId = taskId || params.get('taskId');
  listId = listId || params.get('listId');
  projectId = projectId || params.get('projectId');

  return { view, taskId, listId, projectId };
}

/**
 * Parse calendar route segments
 */
function parseCalendarRoute(segments: string[], params: URLSearchParams) {
  let date: string | null = null;
  let eventId: string | null = null;
  let view: 'day' | 'week' | 'month' | 'year' | 'agenda' | null = null;

  if (segments.length > 0) {
    // Check for view mode: /calendar/week, /calendar/month, etc.
    const firstSegment = segments[0];
    if (['day', 'week', 'month', 'year', 'agenda'].includes(firstSegment)) {
      view = firstSegment as 'day' | 'week' | 'month' | 'year' | 'agenda';

      // Check for date after view: /calendar/week/2026-01-15
      if (segments.length > 1 && segments[1].match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = segments[1];
      }
    } else if (firstSegment === 'event' && segments[1]) {
      eventId = decodeURIComponent(segments[1]);
    } else if (firstSegment.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Legacy format: /calendar/2026-01-15
      date = firstSegment;
    }
  }

  // Check query params
  date = date || params.get('date');
  eventId = eventId || params.get('eventId');
  const viewParam = params.get('view');
  if (viewParam && ['day', 'week', 'month', 'year', 'agenda'].includes(viewParam)) {
    view = view || (viewParam as 'day' | 'week' | 'month' | 'year' | 'agenda');
  }

  return { date, eventId, view };
}

/**
 * Parse notes route segments
 */
function parseNotesRoute(segments: string[], params: URLSearchParams) {
  let noteId: string | null = null;
  let tagId: string | null = null;

  if (segments.length > 0) {
    const firstSegment = segments[0];
    if (firstSegment === 'tag' && segments[1]) {
      // /notes/tag/{tagId}
      tagId = decodeURIComponent(segments[1]);
    } else if (firstSegment) {
      // /notes/{noteId}
      noteId = decodeURIComponent(firstSegment);
    }
  }

  // Also check query params (tagId can be a query param alongside a noteId path)
  noteId = noteId || params.get('noteId');
  tagId = tagId || params.get('tag') || params.get('tagId');

  return { noteId, tagId };
}

/**
 * Build hash URL for tasks workspace
 */
export function buildTasksPath(options: {
  view?: TodoPrimaryView | null;
  taskId?: string | null;
  listId?: string | null;
  projectId?: string | null;
}): string {
  const { view, taskId, listId, projectId } = options;

  if (taskId) {
    return `#/tasks/${encodeURIComponent(taskId)}`;
  }

  if (listId) {
    return `#/tasks/list/${encodeURIComponent(listId)}`;
  }

  if (projectId) {
    return `#/tasks/project/${encodeURIComponent(projectId)}`;
  }

  if (view && view !== 'inbox') {
    return `#/tasks/${view}`;
  }

  return '#/tasks';
}

/**
 * Build hash URL for calendar workspace
 */
export function buildCalendarPath(options: {
  date?: string | null;
  eventId?: string | null;
  view?: 'day' | 'week' | 'month' | 'year' | 'agenda' | null;
}): string {
  const { date, eventId, view } = options;

  if (eventId) {
    return `#/calendar/event/${encodeURIComponent(eventId)}`;
  }

  // Build path with view
  if (view) {
    if (date) {
      return `#/calendar/${view}/${date}`;
    }
    return `#/calendar/${view}`;
  }

  // Fallback to date-only or root
  if (date) {
    return `#/calendar/${date}`;
  }

  return '#/calendar';
}

/**
 * Build hash URL for notes workspace
 */
export function buildNotesPath(options: {
  noteId?: string | null;
  tagId?: string | null;
}): string {
  const { noteId, tagId } = options;

  if (noteId) {
    // Preserve tag filter as query param when viewing a specific note
    if (tagId) {
      return `#/notes/${encodeURIComponent(noteId)}?tag=${encodeURIComponent(tagId)}`;
    }
    return `#/notes/${encodeURIComponent(noteId)}`;
  }

  if (tagId) {
    return `#/notes/tag/${encodeURIComponent(tagId)}`;
  }

  return '#/notes';
}

/**
 * Build hash URL for mail workspace
 */
export function buildMailPath(options: {
  accountId?: string | null;
  folderId?: string | null;
  emailId?: string | null;
  threadId?: string | null;
  detailAccountId?: string | null;
  sender?: string | null;
  labelFilter?: string | null;
}): string {
  const { accountId, folderId, emailId, threadId, detailAccountId, sender, labelFilter } = options;

  let path = '#/';

  // Build path segments
  if (accountId) {
    path += `account/${encodeURIComponent(accountId)}`;
    if (folderId) {
      path += `/folder/${encodeURIComponent(folderId)}`;
    }
    if (threadId) {
      path += `/thread/${encodeURIComponent(threadId)}`;
    } else if (emailId) {
      path += `/email/${encodeURIComponent(emailId)}`;
    }
  } else if (threadId) {
    path += `thread/${encodeURIComponent(threadId)}`;
  } else if (emailId) {
    path += `email/${encodeURIComponent(emailId)}`;
  }

  // Build query params
  const params = new URLSearchParams();
  if (detailAccountId) params.set('detailAccountId', detailAccountId);
  if (sender) params.set('sender', sender);
  if (labelFilter) params.set('label', labelFilter);

  const queryString = params.toString();
  if (queryString) {
    path += '?' + queryString;
  }

  return path;
}

/**
 * Determine if current URL represents the given workspace
 */
export function isWorkspaceUrl(pathname: string, workspace: Workspace): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return workspace === 'mail';

  const firstSegment = segments[0];
  if (firstSegment === 'tasks') return workspace === 'todo';
  if (firstSegment === 'calendar') return workspace === 'calendar';
  if (firstSegment === 'notes') return workspace === 'notes';
  return workspace === 'mail';
}
